/**
 * WarehouseRateCardDialog — per-warehouse transfer rate card editor.
 *
 * Small settings panel so admins can adjust the freight + handling
 * defaults that drive the transfer settlement invoice math, without
 * hitting the API. Loads on open, saves via PUT.
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Coins, FloppyDisk } from '@phosphor-icons/react';
import { getTransferRateCard, saveTransferRateCard } from '../lib/api';

const CURRENCIES = ['USD', 'AUD', 'EUR', 'GBP', 'CAD', 'NZD', 'AED', 'CNY', 'JPY', 'INR'];

const FIELDS = [
  { key: 'flat_freight_fee', label: 'Flat freight fee', hint: 'Default freight cost per transfer' },
  { key: 'outbound_handling_per_pallet', label: 'Outbound handling · per pallet', hint: 'Charged on ship' },
  { key: 'outbound_handling_per_unit', label: 'Outbound handling · per unit', hint: 'Charged on ship' },
  { key: 'inbound_handling_per_pallet', label: 'Inbound handling · per pallet', hint: 'Charged on receive (reserved)' },
];

const EMPTY = {
  flat_freight_fee: 0,
  outbound_handling_per_pallet: 0,
  outbound_handling_per_unit: 0,
  inbound_handling_per_pallet: 0,
  currency: 'USD',
};

export default function WarehouseRateCardDialog({ open, onClose, warehouse }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !warehouse?.id) return;
    (async () => {
      setLoading(true);
      try {
        const r = await getTransferRateCard(warehouse.id);
        setForm({ ...EMPTY, ...r.data });
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Could not load rate card');
      } finally { setLoading(false); }
    })();
  }, [open, warehouse?.id]);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        currency: form.currency || 'USD',
        flat_freight_fee: Number(form.flat_freight_fee) || 0,
        outbound_handling_per_pallet: Number(form.outbound_handling_per_pallet) || 0,
        outbound_handling_per_unit: Number(form.outbound_handling_per_unit) || 0,
        inbound_handling_per_pallet: Number(form.inbound_handling_per_pallet) || 0,
      };
      await saveTransferRateCard(warehouse.id, payload);
      toast.success('Rate card saved');
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="rate-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins size={20} weight="fill" /> Transfer rate card · {warehouse?.code}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Defaults auto-populate settlement invoices for outbound transfers from this warehouse. Any field can be
            overridden per-transfer.
          </p>
        </DialogHeader>

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <div className="space-y-3" data-testid="rate-card-form">
            <div>
              <Label className="text-xs">Currency</Label>
              <Select value={form.currency} onValueChange={(v) => update('currency', v)}>
                <SelectTrigger data-testid="rate-card-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-xs">{f.label}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form[f.key] ?? 0}
                  onChange={(e) => update(f.key, e.target.value)}
                  data-testid={`rate-card-${f.key}`}
                />
                <p className="text-[11px] text-muted-foreground mt-0.5">{f.hint}</p>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading} data-testid="rate-card-save-btn">
            <FloppyDisk size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
